import React, { useState, useEffect, Fragment } from "react";
import { Spinner, Alert } from "react-bootstrap";
import axiosInstance from "../../services/AxiosInstance";
import swal from "sweetalert";

const AntiBanSettings = () => {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  const [enabled, setEnabled]               = useState(false);
  const [delayMin, setDelayMin]             = useState(30);
  const [delayMax, setDelayMax]             = useState(90);
  const [burstSize, setBurstSize]           = useState(50);
  const [burstCooldown, setBurstCooldown]   = useState(300);
  const [typing, setTyping]                 = useState(false);
  const [spintax, setSpintax]               = useState(true);
  const [autoPause, setAutoPause]           = useState(true);
  const [autoPauseThreshold, setAutoPauseThreshold] = useState(5);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("packs/settings/anti-ban");
      if (data.status && data.settings) {
        const s = data.settings;
        setEnabled(s.antiBanEnabled);
        setDelayMin(s.delayMinSeconds);
        setDelayMax(s.delayMaxSeconds);
        setBurstSize(s.burstSize);
        setBurstCooldown(s.burstCooldownSeconds);
        setTyping(s.typingSimulation || false);
        setSpintax(s.spintaxEnabled !== false);
        setAutoPause(s.autoPauseEnabled !== false);
        setAutoPauseThreshold(s.autoPauseThreshold || 5);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (Number(delayMax) < Number(delayMin)) {
      swal("Invalid", "Max delay must be greater than min delay", "error");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosInstance.post("packs/settings/anti-ban", {
        antiBanEnabled:       enabled,
        delayMinSeconds:      Number(delayMin),
        delayMaxSeconds:      Number(delayMax),
        burstSize:            Number(burstSize),
        burstCooldownSeconds: Number(burstCooldown),
        typingSimulation:     typing,
        spintaxEnabled:       spintax,
        autoPauseEnabled:     autoPause,
        autoPauseThreshold:   Number(autoPauseThreshold),
      });
      if (data.status) {
        swal("Saved", "Anti-ban settings updated. Changes apply on next campaign.", "success");
      } else {
        swal("Error", data.message, "error");
      }
    } catch (e) { swal("Error", e.message, "error"); }
    setSaving(false);
  };

  // Quick estimate of throughput
  const avgDelay = (Number(delayMin) + Number(delayMax)) / 2;
  const typingOverhead = typing ? 2.75 : 0; // avg typing duration in sec
  const perMsgSec = avgDelay + typingOverhead;
  const msgsPerHour = Math.floor(3600 / perMsgSec);
  const cooldownsPerHour = Math.floor(msgsPerHour / Number(burstSize || 1));
  const adjustedMsgsPerHour = Math.max(0, msgsPerHour - (cooldownsPerHour * Number(burstCooldown) / perMsgSec));

  if (loading) {
    return <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>;
  }

  return (
    <Fragment>
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div>
              <h4 className="card-title">Anti-Ban Settings</h4>
              <small className="text-muted">
                Smart delays + cooldowns to reduce ban risk on bulk WhatsApp sends.
              </small>
            </div>
          </div>
          <div className="card-body">

            <Alert variant={enabled ? "success" : "warning"} className="mb-4">
              <strong>Status: {enabled ? "🟢 ACTIVE" : "🟡 DISABLED"}</strong><br />
              {enabled
                ? "Smart delays are applied to every message send across all users."
                : "Currently using legacy fast mode (100-1000ms delay). High ban risk on bulk sends."
              }
            </Alert>

            <form onSubmit={onSave}>

              <div className="row mb-4">
                <div className="form-group col-md-12">
                  <label className="text-black font-w500">Master Switch</label>
                  <select className="form-control" value={enabled ? "ON" : "OFF"}
                    onChange={e => setEnabled(e.target.value === "ON")}>
                    <option value="OFF">OFF — Legacy fast mode (current behavior)</option>
                    <option value="ON">ON — Apply smart delays globally</option>
                  </select>
                </div>
              </div>

              <hr />

              <h6 className="text-primary mb-3">Per-Message Delay</h6>
              <div className="row">
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Min Delay (seconds)</label>
                  <input type="number" className="form-control" min="1"
                    value={delayMin} onChange={e => setDelayMin(e.target.value)} />
                  <small className="text-muted">Lowest random delay between messages.</small>
                </div>
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Max Delay (seconds)</label>
                  <input type="number" className="form-control" min="1"
                    value={delayMax} onChange={e => setDelayMax(e.target.value)} />
                  <small className="text-muted">Highest random delay between messages.</small>
                </div>
              </div>

              <hr />

              <h6 className="text-primary mb-3">Burst Cooldown (Mimic Human Breaks)</h6>
              <div className="row">
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Burst Size (messages)</label>
                  <input type="number" className="form-control" min="1"
                    value={burstSize} onChange={e => setBurstSize(e.target.value)} />
                  <small className="text-muted">After every N messages, take a longer cooldown.</small>
                </div>
                <div className="form-group col-md-6">
                  <label className="text-black font-w500">Cooldown Duration (seconds)</label>
                  <input type="number" className="form-control" min="0"
                    value={burstCooldown} onChange={e => setBurstCooldown(e.target.value)} />
                  <small className="text-muted">Long pause between bursts (default 300s = 5 min).</small>
                </div>
              </div>

              <hr />

              <hr />

              <h6 className="text-primary mb-3">Behavior Mimicry</h6>
              <div className="row">
                <div className="form-group col-md-12">
                  <label className="text-black font-w500">Typing Indicator Before Send</label>
                  <select className="form-control" value={typing ? "ON" : "OFF"}
                    onChange={e => setTyping(e.target.value === "ON")}>
                    <option value="OFF">OFF — Send messages instantly</option>
                    <option value="ON">ON — Show "typing…" 1.5-4s before each message</option>
                  </select>
                  <small className="text-muted">
                    Real users type before sending. Bots usually don't. This signal alone reduces ban risk significantly.
                    Adds ~3 seconds per message to the campaign duration.
                  </small>
                </div>
              </div>

              <hr />

              <h6 className="text-primary mb-3">Content Variation (Spintax)</h6>
              <div className="row">
                <div className="form-group col-md-12">
                  <label className="text-black font-w500">Spintax Message Spinning</label>
                  <select className="form-control" value={spintax ? "ON" : "OFF"}
                    onChange={e => setSpintax(e.target.value === "ON")}>
                    <option value="ON">ON — Parse {"{Hi|Hello}"} syntax for unique variations</option>
                    <option value="OFF">OFF — Send message text as-is</option>
                  </select>
                  <small className="text-muted">
                    Users can write <code>{"{Hi|Hello|Hey} {{name}}, hope you're well"}</code> to give every recipient a unique variation. Messages without spintax syntax are sent unchanged. Defeats WhatsApp's content-fingerprint detection.
                  </small>
                </div>
              </div>

              <hr />

              <h6 className="text-primary mb-3">Account Safety</h6>
              <div className="row">
                <div className="form-group col-md-8">
                  <label className="text-black font-w500">Auto-Pause on Failures</label>
                  <select className="form-control" value={autoPause ? "ON" : "OFF"}
                    onChange={e => setAutoPause(e.target.value === "ON")}>
                    <option value="ON">ON — Stop campaign if too many failures in a row</option>
                    <option value="OFF">OFF — Continue sending even if all fail</option>
                  </select>
                  <small className="text-muted">
                    Prevents account meltdown when WhatsApp shadow-bans an instance mid-campaign.
                    Strongly recommended.
                  </small>
                </div>
                <div className="form-group col-md-4">
                  <label className="text-black font-w500">Failure Threshold</label>
                  <input type="number" className="form-control" min="1" max="50"
                    value={autoPauseThreshold} onChange={e => setAutoPauseThreshold(e.target.value)} />
                  <small className="text-muted">Pause after N consecutive fails.</small>
                </div>
              </div>

              <hr />

              <Alert variant="info">
                <strong>Estimated throughput at these settings:</strong><br />
                ~{adjustedMsgsPerHour.toFixed(0)} messages/hour (after cooldowns)<br />
                ~{(adjustedMsgsPerHour * 8).toFixed(0)} messages/8h workday
              </Alert>

              <div className="mt-2">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Save Settings"}
                </button>
              </div>

            </form>

            <hr className="mt-4" />

            <h6 className="text-primary mb-3">Recommended Presets</h6>
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="card border" style={{cursor:"pointer"}}
                  onClick={() => { setEnabled(true); setDelayMin(15); setDelayMax(45); setBurstSize(30); setBurstCooldown(120); setTyping(false); }}>
                  <div className="card-body p-3">
                    <strong>🚀 Aggressive</strong>
                    <p className="mb-0 text-muted small">15-45s delay · burst 30 · 2min cooldown<br />
                      ~150 msgs/h · higher ban risk</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card border" style={{cursor:"pointer"}}
                  onClick={() => { setEnabled(true); setDelayMin(30); setDelayMax(90); setBurstSize(50); setBurstCooldown(300); setTyping(true); }}>
                  <div className="card-body p-3">
                    <strong>⚖️ Balanced (Recommended)</strong>
                    <p className="mb-0 text-muted small">30-90s delay · burst 50 · 5min cooldown<br />
                      ~50 msgs/h · safe</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="card border" style={{cursor:"pointer"}}
                  onClick={() => { setEnabled(true); setDelayMin(60); setDelayMax(180); setBurstSize(20); setBurstCooldown(600); setTyping(true); }}>
                  <div className="card-body p-3">
                    <strong>🛡️ Conservative</strong>
                    <p className="mb-0 text-muted small">60-180s delay · burst 20 · 10min cooldown<br />
                      ~25 msgs/h · safest</p>
                  </div>
                </div>
              </div>
            </div>
            <small className="text-muted">Click a preset to load values, then Save.</small>

          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AntiBanSettings;
